import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useHistory } from "react-router";

import {
  BAND_ACTIONS,
  IBirthdayEvent,
  IEvent,
  IEventStatus,
  PeopleBlockStatus,
} from "Bands/types";
import { ITheme, IAPIState } from "shared/types";
import { copy } from "shared/assets";
import {
  CalendarListBirthday,
  CalendarListStatus,
  CalendarListTitle,
  Table,
  TableColumn,
  TableSkeleton,
} from "shared/components";
import {
  useBandContext,
  useCalendarContext,
  useEventContext,
} from "Bands/contexts";
import { dataMetaSelector } from "Bands/state";
import { buildUrl, mapUserPermissions } from "shared/utils";
import { BAND_PATHS } from "Bands/router";
import {
  getEventActions,
  getEventBandId,
  getPeopleBlockStatus,
} from "Bands/utils";
import { FlexContainer, Icon, MutedText, TinyText } from "shared/styles";
import { getStatus } from "shared/styles/defaults";

const listCopy = copy.calendar.list;

interface CalendarListTableProps {
  handleDelete?: (event: IEvent) => void;
  hideHeader?: boolean;
  showActions?: boolean;
  showCheckboxes?: boolean;
  handleCheckboxChange?: (value: string) => void;
  selectedEvents?: string[];
  hideCurrentEvent?: boolean;
  hideBirthdays?: boolean;
}

export const CalendarListTable: FC<CalendarListTableProps> = ({
  handleDelete,
  hideHeader,
  showActions = true,
  showCheckboxes = false,
  handleCheckboxChange,
  selectedEvents,
  hideCurrentEvent = false,
  hideBirthdays = false,
}) => {
  const { bandId, birthdays } = useBandContext();
  const { events, date } = useCalendarContext();
  const { event: currentEvent } = useEventContext();
  const history = useHistory();

  const { api: apiMeta } = useSelector(
    dataMetaSelector(BAND_ACTIONS.getEvents)
  );

  const getRowActions = useCallback(
    (eventId) => {
      const event = events.find((event) => event.id === eventId);

      if (!event) return [];

      const bandId = getEventBandId(event);

      const editUrl = buildUrl(BAND_PATHS.EDIT_BAND_EVENT, {
        bandId,
        eventId,
      });

      return getEventActions({
        handleEdit: () => history.push(editUrl),
        handleDelete: () => handleDelete?.(event),
        userPermissions: event?.permissions
          ? mapUserPermissions(event.permissions)
          : {},
      });
    },
    [events, history, handleDelete]
  );

  const bdaysThisMonth = useMemo(() => {
    if (hideBirthdays) return [];

    const monthBirthdays = birthdays?.[String(date.month)] || [];

    return monthBirthdays.map((bday) => {
      // change year to current year

      const bdayParts = bday.date.split("-");

      return {
        ...bday,
        date: `${date.year}-${bdayParts[1]}-${bdayParts[2]}`,
      };
    });
  }, [birthdays, date, hideBirthdays]);

  const eventsAndBirthdays = [...events, ...bdaysThisMonth]
    .filter((event) => {
      if (hideCurrentEvent && event.id === currentEvent?.id) return false;
      return true;
    })
    .sort((a: IEvent | IBirthdayEvent, b: IEvent | IBirthdayEvent) => {
      // if both items are events, don't sort
      // events are already sorted in backend
      if ("type" in a && "type" in b) return 0;

      return new Date(a.date).getUTCDate() > new Date(b.date).getUTCDate()
        ? 1
        : -1;
    });

  const [showLoader, setShowLoader] = useState(false);

  useEffect(() => {
    if (apiMeta?.status === IAPIState.pending) {
      setShowLoader(true);
      return;
    }

    setTimeout(() => setShowLoader(false), 300);
  }, [apiMeta?.status]);

  if (showLoader) return <TableSkeleton theme={ITheme.tertiary} />;

  return (
    <Table
      theme={ITheme.tertiary}
      options={{
        hideHeader,
        showActions,
        showCheckboxes,
        handleCheckboxChange,
        selectedEvents,
      }}
      columns={getColumns(events, !bandId, bdaysThisMonth, hideHeader)}
      data={eventsAndBirthdays}
      emptyCopy={listCopy.empty}
      getRowActions={getRowActions}
    />
  );
};

const getColumns = (
  events: IEvent[],
  showBand?: boolean,
  birthdays?: IBirthdayEvent[],
  hideHeader?: boolean
): TableColumn[] => {
  const optionalColumns = [
    {
      Header: listCopy.columns.status,
      accessor: "status",
      minWidth: 200,
      Cell: ({ value }: { value: IEventStatus }) =>
        value ? <CalendarListStatus status={value} /> : null,
    },
    {
      Header: listCopy.columns.myRSVP,
      accessor: "userAttendance",
      minWidth: 200,
      Cell: ({ value }: { value: string }) => {
        const userAttendance = value as PeopleBlockStatus;

        if (!userAttendance) return null;

        const status = getPeopleBlockStatus(userAttendance);

        if (!status) return null;

        return (
          <FlexContainer>
            <Icon
              as={status.icon}
              color={getStatus(status.iconStatus).iconColor}
            />
            <MutedText as={TinyText}>{status.viewLabel}</MutedText>
          </FlexContainer>
        );
      },
    },
  ];

  return [
    {
      Header: "",
      accessor: "id",
      Cell: ({ value: id }) => {
        const event = events.find((event) => event.id === id);

        if (event) {
          const { title, type, date, band, bandName } = event;

          return (
            <CalendarListTitle
              id={id}
              title={title}
              type={type}
              date={date}
              band={band}
              bandName={bandName}
              showBand={showBand}
              showAsSpan={hideHeader}
            />
          );
        }

        const bday = birthdays?.find((day) => day.id === id);

        if (bday) {
          return <CalendarListBirthday birthday={bday} />;
        }

        return null;
      },
    },
    ...(hideHeader ? [] : optionalColumns),
  ];
};
